/**
 * brandStatus — the ONE brand-status vocabulary (WS-B B-10 / AUD-6 MIN-7).
 *
 * `BrandsPanel` (the customer page) and `BrandsAdmin` (Editor → Brands) each declared their
 * own `STATUS_BADGE` / `STATUS_LABEL` / `FILTERS` triple. The labels and the colours happened
 * to agree; the ORDER did not:
 *
 *   customer: All · Draft · Pending · Approved · Rejected
 *   admin:    All · Pending · Approved · Rejected · Draft
 *
 * Same filter, same page title, two orders — so muscle memory built on one surface misfires
 * on the other, and nothing but luck was keeping the labels and colours in step either.
 *
 * LIFECYCLE order wins (draft → pending → approved → rejected), which is the customer page's:
 * it is the order the thing actually happens in, so a reader can predict it without having
 * memorised it. The admin page is the one that changes.
 */
import type { BrandStatus } from "@genz/platform";

/** Every real status, in the order a brand moves through them. */
export const BRAND_STATUS_ORDER: readonly BrandStatus[] = ["draft", "pending", "approved", "rejected"];

/** The filter chips: "All" first, then the lifecycle. */
export type BrandStatusFilter = "all" | BrandStatus;
export const BRAND_STATUS_FILTERS: readonly BrandStatusFilter[] = ["all", ...BRAND_STATUS_ORDER];

export const BRAND_STATUS_LABEL: Record<BrandStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

/** One tone per status, so "awaiting review" and "rejected" are never the same pill. */
export const BRAND_STATUS_BADGE: Record<BrandStatus, string> = {
  draft: "badge--neutral",
  pending: "badge--warn",
  approved: "badge--success",
  rejected: "badge--danger",
};

/** The label for a filter chip — "All" for the catch-all, the status label otherwise. */
export function brandFilterLabel(f: BrandStatusFilter): string {
  return f === "all" ? "All" : BRAND_STATUS_LABEL[f];
}
